// The workshop's frame: the pack bar (which pack every tab shows), the tabs, and the status bar (delve-sim, the live
// link to a running game, the workshop pass). The sim and the live link are shared by every tab.
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, ScrollRestoration } from "react-router";
import { useAuth } from "@/shared/auth/useAuth";
import { useWorkshopPass } from "@/features/workshop/data";
import { PackProvider, usePack } from "@/features/workshop/pack";
import { LiveLinkProvider, useLiveLink } from "@/lib/livelink/LiveLinkProvider";
import { SimProvider, useSim } from "@/lib/sim/SimProvider";
import styles from "./Layout.module.css";

export const TABS = ["dungeons", "validation", "heroes", "bestiary", "weapons", "tuning", "live", "game-settings", "settings"] as const;

function Brand() {
  const { t } = useTranslation();
  return (
    <Link to="/" className={styles.brand} aria-label={t("brand.home")}>
      <svg viewBox="0 0 64 64" width="26" height="26" aria-hidden>
        <path d="M32 8 52 52H12Z" fill="none" stroke="currentColor" strokeWidth="5" strokeLinejoin="round" />
        <circle cx="32" cy="38" r="6" fill="var(--gold)" />
      </svg>
      <span>Delve</span> {t("brand.name")}
    </Link>
  );
}

function PackBar() {
  const { t } = useTranslation("workshop");
  const { pack, packs, setPack, loading } = usePack();
  return (
    <label className={styles.pack}>
      <span>{t("pack.label")}</span>
      <select value={pack} onChange={(e) => setPack(e.target.value)} disabled={loading || packs.length < 2}>
        {loading && <option value={pack}>{t("pack.loading")}</option>}
        {packs.map((p) => <option key={p.id} value={p.id}>{p.game ? `${p.name} — ${p.game}` : p.name}</option>)}
      </select>
    </label>
  );
}

function StatusBar() {
  const { t } = useTranslation("workshop");
  const pass = useWorkshopPass();
  const sim = useSim();
  const link = useLiveLink();
  const simState = sim.status === "ready" ? "ok" : sim.status === "loading" ? "idle" : "error";
  const linkState = link.status === "connected" ? "ok" : link.status === "error" ? "error" : "idle";
  return (
    <footer className={styles.status} aria-label={t("status.label")}>
      <span title={sim.error}>
        <i className={styles.dot} data-state={simState} aria-hidden />{t("status.sim")}:{" "}
        {sim.status === "ready" ? t("status.simReady", { count: sim.biomes.length }) : t(`status.sim_${sim.status}`)}
      </span>
      <span title={link.error ?? undefined}>
        <i className={styles.dot} data-state={linkState} aria-hidden />{t("status.live")}:{" "}
        {link.status === "connected"
          ? t("status.liveOn", { game: link.hello?.game ?? "", pack: link.hello?.pack ?? "", port: link.settings.port })
          : <Link to="/live">{t(`status.live_${link.status}`)}</Link>}
      </span>
      <span className={styles.statusEnd}>
        <i className={styles.dot} data-state={pass.isSuccess ? "ok" : pass.isError ? "error" : "idle"} aria-hidden />
        {t("status.pass")}: {pass.isSuccess ? t("status.passOk") : pass.isError ? t("status.passFailed") : "…"}
      </span>
    </footer>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const tw = useTranslation("workshop").t;
  const { signOut } = useAuth();
  return (
    <PackProvider>
      <SimProvider>
      <LiveLinkProvider>
      <div className={styles.shell}>
        <a href="#main" className={styles.skip}>{t("nav.skip")}</a>
        <header className={styles.header}>
          <div className={styles.bar}>
            <Brand />
            <PackBar />
            <button type="button" className={styles.signOut} onClick={() => void signOut()}>{t("nav.signOut")}</button>
          </div>
          <nav aria-label={t("nav.main")} className={styles.tabs}>
            <NavLink to="/" end className={styles.tab}>{tw("tabs.dashboard")}</NavLink>
            {TABS.map((tab) => <NavLink key={tab} to={`/${tab}`} className={styles.tab}>{tw(`tabs.${tab}`)}</NavLink>)}
          </nav>
        </header>
        <main id="main" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
        <StatusBar />
        <ScrollRestoration />
      </div>
      </LiveLinkProvider>
      </SimProvider>
    </PackProvider>
  );
}
